import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, Truck, Calendar, ShieldCheck, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 2000;
const MAX_POLLS = 8;

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [sub, setSub] = useState(null);
  const [polling, setPolling] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session id. If you completed checkout, please check your email for confirmation.');
      setPolling(false);
      return;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/status/${sessionId}`);
        if (cancelled) return;
        setSub(r.data);
        if (r.data?.status === 'active' || r.data?.status === 'expired') {
          setPolling(false);
          return;
        }
        if (++attempts >= MAX_POLLS) {
          setPolling(false);
          return;
        }
        setTimeout(poll, POLL_MS);
      } catch (err) {
        if (cancelled) return;
        if (++attempts >= MAX_POLLS) {
          setError('We couldn\'t verify the subscription status yet. Please check your inbox for the Stripe receipt.');
          setPolling(false);
          return;
        }
        setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-[calc(100vh-160px)] py-20 px-4" style={{ backgroundColor: '#f7f9f3' }} data-testid="subscription-success-page">
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-xl border border-emerald-100 overflow-hidden">
          <div className="px-8 py-7 text-white text-center" style={{ background: 'linear-gradient(135deg,#2d5016 0%,#3a6b1d 100%)' }}>
            {polling ? (
              <Loader2 className="w-12 h-12 mx-auto mb-3 animate-spin" />
            ) : sub?.status === 'active' ? (
              <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
            ) : (
              <Mail className="w-12 h-12 mx-auto mb-3" />
            )}
            <h1 className="text-3xl font-bold mb-1">{polling ? 'Confirming your subscription\u2026' : sub?.status === 'active' ? 'You\'re subscribed!' : 'Almost there'}</h1>
            <p className="text-sm opacity-90">{polling ? 'This usually takes a few seconds.' : sub?.status === 'active' ? 'Welcome to the IIY family. Your first bottle is on its way.' : 'We\'re finalising the details \u2014 a Stripe receipt will land in your inbox shortly.'}</p>
          </div>

          <div className="px-8 py-7 space-y-5">
            {error && (
              <div className="text-sm rounded-lg px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-800" data-testid="subscription-error">{error}</div>
            )}

            {sub && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="subscription-details">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Plan</div>
                  <div className="text-sm font-semibold text-slate-800">£{(sub.amount_per_period || 0).toFixed(2)} / {sub.interval}</div>
                  <div className="text-xs text-slate-600 mt-0.5">31.10% off £44.99 RRP \u00b7 1 bottle (60 capsules)</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1 flex items-center gap-1.5"><Truck className="w-3 h-3" /> Auto-ship</div>
                  <div className="text-sm font-semibold text-slate-800">Free UK Delivery</div>
                  <div className="text-xs text-slate-600 mt-0.5">Same date every month \u00b7 cancel any time</div>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-emerald-100 p-4 bg-white">
              <div className="flex items-start gap-2.5">
                <ShieldCheck className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700 leading-relaxed">
                  <strong>Manage your subscription any time.</strong> Cancel, pause, or update your card and shipping address from the Stripe customer portal we'll email you. You can also write to <a href="mailto:support@iiypv.com" className="text-emerald-700 hover:underline">support@iiypv.com</a> with your name and email and we'll handle it manually.
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/" className="px-5 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium" data-testid="subscription-back-home">Back to home</Link>
              <Link to="/track-order" className="px-5 py-2.5 rounded-lg border border-emerald-700 text-emerald-700 hover:bg-emerald-50 text-sm font-medium" data-testid="subscription-track-link">Track shipments</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
